/**
 * The read-only fact cards of the Inventar detail page — Inventar 2.0, S6b.
 *
 * Three surfaces that used to be three near-identical dark boxes with an
 * uppercase micro-heading and a two-column <dl>: Stammdaten, Produkt /
 * Lieferant and Bestand. They keep exactly the fields they had — this slice
 * changes how they read, not what they say — but the page is a READING
 * surface, so they now read as key/value pairs rather than as a form that
 * happens to be disabled.
 *
 * TOKENS ONLY: no hex values, no !important patch layer, and therefore a
 * light mode that works because the theme says so rather than because
 * index.css overrides every colour back.
 */
import React from "react";
import { Boxes, ExternalLink, Layers, MapPin } from "lucide-react";

/** Shared card chrome, so the three cards cannot drift apart. */
export function DetailCard({ title, action = null, children, className = "" }) {
  return (
    <section
      className={`rounded-[10px] border border-border bg-card p-4 ${className}`}
      aria-label={title}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
          {title}
        </h2>
        {action}
      </div>
      <div className="mt-3.5">{children}</div>
    </section>
  );
}

/**
 * One key/value row. The value falls back to an em dash rather than
 * disappearing, so the shape of the card does not change with the data.
 */
function Fact({ label, children, mono = false }) {
  const empty = children == null || children === "" || children === false;
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={`m-0 mt-1 break-words text-[13px] font-medium text-foreground ${
          mono ? "font-mono text-[12px]" : ""
        }`}
      >
        {empty ? <span className="text-muted-foreground">—</span> : children}
      </dd>
    </div>
  );
}

/**
 * S8: two decimals, always.
 *
 * This used to pass only `maximumFractionDigits`, so the very same price read
 * "CHF 31.50" in the overview list and "CHF 31.5" here — and a round number
 * lost its decimals entirely ("CHF 120"). The list and the CSV have always
 * printed two, and so does the reference.
 *
 * Returning null rather than an em dash for a missing value is deliberate and
 * unchanged: `Fact` renders its own dash, and a string here would be a second
 * one. A price of 0 is a value, not a gap, and still prints as CHF 0.00.
 */
function formatChf(value) {
  if (value == null || value === "") return null;
  return `CHF ${Number(value).toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Stammdaten — what the article IS.
 *
 * `ageLabel` and `retiredAtLabel` are passed in already formatted: both are
 * derived on the page from helpers that also serve the assignment rows, and
 * duplicating that arithmetic here would be a second place to get it wrong.
 */
export function InventoryDetailInfoCard({ item, ageLabel, retiredAtLabel, purchaseDateLabel }) {
  return (
    <DetailCard title="Stammdaten">
      <dl className="grid gap-3.5 sm:grid-cols-2">
        <Fact label="Inventarnummer" mono>
          {item.inventory_number}
        </Fact>
        <Fact label="Kategorie">{item.category}</Fact>
        <Fact label="Marke">{item.brand}</Fact>
        <Fact label="Zustand">{item.condition}</Fact>
        {purchaseDateLabel ? <Fact label="Kaufdatum">{purchaseDateLabel}</Fact> : null}
        {item.purchase_date ? <Fact label="Alter">{ageLabel}</Fact> : null}
        {item.retired_at ? <Fact label="Ausgemustert am">{retiredAtLabel}</Fact> : null}
      </dl>
    </DetailCard>
  );
}

/** Produkt / Lieferant — where the article CAME FROM. */
export function InventoryDetailSupplierCard({ item }) {
  const href = String(item.product_url || "").trim();
  return (
    <DetailCard title="Produkt / Lieferant">
      <dl className="grid gap-3.5 sm:grid-cols-2">
        <Fact label="Lieferanten-Artikelnr." mono>
          {item.supplier_article_number}
        </Fact>
        <Fact label="Gekauft bei">{item.purchased_from}</Fact>
        <Fact label="Anschaffung">{formatChf(item.purchase_price_chf)}</Fact>
        <Fact label="Produkt-URL">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[6px] border border-border bg-muted px-2 py-1 text-[12px] font-semibold text-primary transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
              Produkt öffnen
            </a>
          ) : null}
        </Fact>
      </dl>
    </DetailCard>
  );
}

/**
 * Bestand — three numbers, printed exactly as the backend sent them.
 *
 * No client arithmetic and no derived percentage: total, assigned and
 * available come off the item contract, and the service already clamps them.
 * No progress bar either — with three plain integers it would decorate rather
 * than inform, and it would quietly imply assigned+available==total, which the
 * clamp does not promise.
 *
 * S7c: the three tiles follow the reference's stock block — a tinted icon
 * tile, the label, a large number and one line of explanation each. The icons
 * are the ones the OVERVIEW already uses for the same three concepts
 * (Layers / MapPin / Boxes), so the two pages name a number the same way.
 *
 * The label, the number and the hint are three separate text nodes on purpose:
 * U19 reads them with getByText("Gesamt") / getByText("14"), so no hint may
 * repeat a label or contain a bare figure.
 */
export function InventoryStockCard({ total, assigned, available }) {
  const rows = [
    {
      label: "Gesamt",
      value: total,
      hint: "Alle vorhandenen Einheiten",
      icon: Layers,
      tone: "text-foreground",
      tile: "bg-muted text-muted-foreground",
    },
    {
      label: "Zugeordnet",
      value: assigned,
      hint: "In Units und Räumen",
      icon: MapPin,
      tone: "text-foreground",
      tile: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    },
    {
      label: "Frei",
      value: available,
      hint: "Noch nicht zugeordnet",
      icon: Boxes,
      tone: available > 0 ? "text-primary" : "text-muted-foreground",
      tile:
        available > 0
          ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
          : "bg-muted text-muted-foreground",
    },
  ];
  return (
    <DetailCard title="Bestand">
      <dl className="m-0 grid gap-3 sm:grid-cols-3">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center gap-3 rounded-[10px] border border-border bg-muted/40 px-3.5 py-3"
          >
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${r.tile}`}
              aria-hidden
            >
              <r.icon className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <dt className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
                {r.label}
              </dt>
              <dd className={`m-0 font-mono text-[24px] font-semibold leading-tight tabular-nums ${r.tone}`}>
                {r.value}
              </dd>
              <p className="m-0 truncate text-[10.5px] leading-[1.4] text-muted-foreground">
                {r.hint}
              </p>
            </div>
          </div>
        ))}
      </dl>
    </DetailCard>
  );
}
